import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { LabelTextGroup } from '@components/label-text-group';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { DescriptionFormField } from 'ui/journey-builder/node-types/common';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionSidebar,
  useEditingContext,
} from '../common';
import {
  DurationBreakdown,
  DurationFields,
  DurationRequirement,
  DurationSummary,
  WAIT_FOR_MINIMUM_MINUTES,
  getDurationMinutes,
  normalizeDuration,
  toDurationValues,
} from '../duration';
import { ActionTimeWithTimezoneTooltip, newDateIgnoreLocalTimezone } from '../timezone';

const ACTION_TYPE = ACTION_TYPES.wait_for_time;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();
  const data = action.data;
  const scheduledDate = action?.scheduledDate;
  const selectedDate = scheduledDate ? newDateIgnoreLocalTimezone(scheduledDate as string) : null;

  const hours = selectedDate?.getHours().toString();
  const minutes = selectedDate?.getMinutes().toString();
  const [offset] = action?.timezone?.split(' ') ?? [''];

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        progressPercentage=" "
      />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <div>
            <Text font="semibold14" text={t('configuration')} />
            <Text font="regular14" text={t('configurationDescription')} />
          </div>
          <div>
            <DurationBreakdown
              parts={{ days: data.days, hours: data.hours, minutes: data.minutes }}
            />
          </div>
          <Text font="regular14" text={t('beforeContinuing')} />
          {selectedDate && (
            <div>
              <Text font="semibold14" text={t('triggersNextAction')} />
              <ActionTimeWithTimezoneTooltip
                actionText={abbreviateDate(selectedDate, 'en', 'datetime')}
                hour={hours as string}
                minute={minutes as string}
                journeyTimezoneOffset={offset}
              />
            </div>
          )}
        </Stack>
      </HideContainer>
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type WaitForData = {
  days: string;
  hours: string;
  minutes: string;
  description: string;
};

const Form = createForm<WaitForData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const days = (formStore.useFieldValue('days') ?? '') as string;
  const hours = (formStore.useFieldValue('hours') ?? '') as string;
  const minutes = (formStore.useFieldValue('minutes') ?? '') as string;
  const defaults = normalizeDuration({
    days: action.values?.days?.value,
    hours: action.values?.hours?.value,
    minutes: action.values?.minutes?.value,
  });
  const durationMinutes = getDurationMinutes({ days, hours, minutes });
  const isValid = durationMinutes >= WAIT_FOR_MINIMUM_MINUTES;
  const isDurationDirty = durationMinutes !== getDurationMinutes(defaults);

  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: WaitForData) => {
    const values = toDurationValues(data, WAIT_FOR_MINIMUM_MINUTES);
    if (!values) return false;
    return editMutation.mutateAsync({
      values,
      description: data.description,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <Stack space={4}>
          <Text text={t('selectHowLong')} font="regular14" />
          <Text text={t('useWhen')} font="regular14" />
        </Stack>
        <Separator />

        <DurationFields form={Form} defaults={defaults} />
        <DurationSummary variant="wait" parts={{ days, hours, minutes }} />
        {!isValid && <DurationRequirement variant="wait" isDirty={isDurationDirty} />}
      </Stack>
      <FormSidebarFooter
        Form={Form}
        isDirty={isDirty}
        editMutation={editMutation}
        hasNotifyError={!isValid}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionDetails: 'Action details',
    description: 'Description',
    configuration: 'Configuration',
    configurationDescription: 'The wait for action is configured to wait for',
    beforeContinuing: 'Before triggering the following action',
    selectHowLong:
      'Select for how long journey step should wait before triggreing the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    daysPassed: '{days} Days',
    triggersNextAction: 'Triggers next action',
  },
};
